import moment from "moment";

import styles from "./MultiDateSelector.module.scss";

interface IMultiDateSelectorProps {
    id: string;
    from: Date;
    to: Date;
    fromSetTodayLabel: string;
    toSetTodayLabel: string;
    dataCy?: string;

    fromMyleOperation?: number;
    toMyleOperation?: number;

    fullWidth?: boolean;
    cssClass?: string;
    fieldContainerCssClass?: string;

    label?: string;
    fromLabel?: string;
    toLabel?: string;
    fromChange?: (date: Date) => void;
    toChange?: (date: Date) => void;

    errorMessage?: string;
}

class MultiDateSelector extends React.Component<IMultiDateSelectorProps, never> {
    private culture: string;

    private get fromId(): string {
        return this.props.id + "_from";
    }
    private get toId(): string {
        return this.props.id + "_to";
    }

    constructor(props: IMultiDateSelectorProps) {
        super(props);
        this.culture = sessionStore.getUserLanguageCodeFull();
    }

    public render(): JSX.Element {
        const {
            fieldContainerCssClass = "",
            dataCy,
            to,
            from,
            fromSetTodayLabel,
            label,
            toLabel,
            fromLabel,
            fullWidth,
            cssClass,
            toSetTodayLabel
        } = this.props;
        let fromDateInvalid = !!this.props.errorMessage;
        let toDateInvalid = !!this.props.errorMessage;

        if (from && to && !this.props.errorMessage) {
            if (to.toString() && from.toString()) {
                fromDateInvalid = moment(from) > moment(to);
                toDateInvalid = fromDateInvalid;
            }
        }

        let containerCss = "padded-selectors date-selector-container";
        if (fullWidth)
            containerCss += " fw";
        if (cssClass)
            containerCss += ` ${cssClass}`;

        const fromStyle = fromDateInvalid ? "qr invalid" : "qr";
        const toStyle = toDateInvalid ? "qr invalid" : "qr";

        return (
            <div className={containerCss}>
                {label && <label>{label}</label>}
                <div className={`${fromStyle} ${fieldContainerCssClass}`}>
                    {fromLabel &&
                        <label>{fromLabel}</label>
                    }
                    <input type="text" id={this.fromId} className="form-control" data-cy={dataCy ? `${dataCy}-from` : null} />
                    <div className={styles.todayLabelContainer}>
                        <a
                            data-cy={dataCy ? `${dataCy}-from-today` : null}
                            className="xsmall-font"
                            onClick={e => this.setTodayDate(this.fromId, e)}
                        >
                            {fromSetTodayLabel}
                        </a>
                    </div>
                </div>
                <div className={`${toStyle} ${fieldContainerCssClass}`}>
                    {toLabel &&
                        <label>{toLabel}</label>
                    }
                    <input type="text" id={this.toId} className="form-control" data-cy={dataCy ? `${dataCy}-to` : null} />
                    <div className={styles.todayLabelContainer}>
                        <a
                            data-cy={dataCy ? `${dataCy}-to-today` : null}
                            className="xsmall-font paddingtop20"
                            onClick={e => this.setTodayDate(this.toId, e)}
                        >
                            {toSetTodayLabel}
                        </a>
                    </div>
                </div>
                {this.props.errorMessage && (
                    <div className="qr invalid">
                        <p className="text-danger">{this.props.errorMessage}</p>
                    </div>
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        $("#" + this.fromId).kendoDatePicker({
            value: this.props.from ? this.props.from : null,
            culture: this.culture,
            format: "yyyy/MM/dd",
            parseFormats: ["yyyy-MM-dd", "yyyy/MM/dd", "yyyyMMdd"],
            footer: null,
            change: e => this.onDateChanged(this.fromId, e.sender.value(), this.props.fromChange, this.props.fromMyleOperation)
        });

        $("#" + this.toId).kendoDatePicker({
            value: this.props.to ? this.props.to : null,
            culture: this.culture,
            format: "yyyy/MM/dd",
            footer: null,
            change: e => this.onDateChanged(this.toId, e.sender.value(), this.props.toChange, this.props.toMyleOperation)
        });
    }

    public componentWillUnmount(): void {
        $("#" + this.fromId).data("kendoDatePicker").destroy();
        $("#" + this.toId).data("kendoDatePicker").destroy();
    }

    public componentDidUpdate(prevProps: IMultiDateSelectorProps): void {
        if (prevProps.from !== this.props.from) {
            const fromPicker = $("#" + this.fromId).data("kendoDatePicker");
            if (fromPicker) {
                fromPicker.value(this.props.from);
            }
        }

        if (prevProps.to !== this.props.to) {
            const toPicker = $("#" + this.toId).data("kendoDatePicker");
            if (toPicker) {
                toPicker.value(this.props.to);
            }
        }
    }

    private onDateChanged(id: string, date: Date, onChange: (date: Date) => void | null, operation: number | null): void {
        const value = date
            ? (id === this.fromId
                ? moment(date).startOf("day").toDate()
                : moment(date).endOf("day").toDate()
            )
            : null;

        if (onChange)
            onChange(value);
        else if (operation)
            sessionStore.dispatch(operation, { value: value });
    }

    private setTodayDate(id: string, e: React.MouseEvent): void {
        e.preventDefault();

        const operation = id === this.fromId
            ? this.props.fromMyleOperation
            : this.props.toMyleOperation;
        const value = id === this.fromId
            ? kendo.toString(new Date(), "yyyy/MM/dd", this.culture)
            : moment(new Date()).endOf("day").toDate();

        if (operation)
            sessionStore.dispatch(operation, { value: value });

        $("#" + id).data("kendoDatePicker").value(kendo.toString(new Date(), "yyyy/MM/dd", this.culture));
        $("#" + id).data("kendoDatePicker").trigger("change");
    }
}

export { MultiDateSelector };
